'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useState, useEffect } from 'react';

import type {
  CategoriesEntity,
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';

import { MobileSearchProvider } from '@eliadmin/core/src/components/header/mobile-search';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import MobileSidebar from '@eliadmin/core/src/components/header/mobile-sidebar';
import { SearchButton } from '@eliadmin/core/src/components/header/search-button';
import { usePathname } from 'next/navigation';
import { HeaderProfileButton } from '@eliadmin/core/src/components/header/header-profile-button';
import { HeaderCartButton } from '@eliadmin/core/src/components/header/header-cart-button';
import { Categories } from './categories';
import { useCurrentStore } from '../../utils/use-store-utils';

export type HeaderMainProps = {
  generalLayout: GeneralLayout['data'];
  menu: Menu[];
  categories: CategoriesEntity[];
};

export function HeaderMain({ generalLayout, menu, categories }: HeaderMainProps) {
  const pathname = usePathname();
  const isHomepage = pathname === '/';

  const [scrolled, setScrolled] = useState(false);
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 1) {
        setIsScrolledUp(false);
      } else if (currentScrollY < lastScrollY && Math.abs(currentScrollY - lastScrollY) > 1) {
        setIsScrolledUp(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const { isEshop } = useCurrentStore();

  if (isEshop)
    return (
      <MobileSearchProvider>
        <header
          className={cn(
            'top-0 z-40 w-full transition-all duration-300 ease-in-out',
            isHomepage ? 'fixed bg-transparent' : 'static',
            !isHomepage && 'bg-black',
            scrolled && 'bg-black',
            scrolled && isScrolledUp && isHomepage && 'bg-black',
          )}
        >
          <div
            className={cn(
              'custom-section',
              'hidden w-full transition-all duration-300 ease-in-out md:flex',
              isHomepage && scrolled && !isScrolledUp && '-mt-24',
            )}
          >
            <div className={cn('custom-container', 'flex items-center justify-between pt-4')}>
              <div className="flex items-center justify-start gap-4 xl:gap-6">
                {menu[0]?.fields?.map(item => (
                  <Link
                    prefetchOnHover
                    key={item.name}
                    href={getSafeHref(item.path ?? '')}
                    className="text-sm font-normal text-white"
                  >
                    {item.name}
                  </Link>
                ))}
                <SearchButton
                  variant="ghost"
                  className={{
                    icon: 'text-white',
                    root: 'hidden h-fit w-fit p-0 hover:bg-transparent lg:flex',
                  }}
                />
              </div>
              <Link prefetchOnHover prefetch={false} href={'/'} aria-label="RSmoto – domov">
                <Image
                  src={generalLayout.header?.logo?.url ?? '/images/placeholder.png'}
                  alt={resolveImageAlt(
                    generalLayout?.header?.logo,
                    generalLayout?.contact?.company,
                  )}
                  width={0}
                  height={0}
                  className="min-h-[72px] w-auto object-contain object-center"
                />
              </Link>
              <div className="flex items-center justify-end gap-4 lg:w-[376px] xl:gap-6">
                <SearchButton
                  variant="ghost"
                  className={{
                    icon: 'text-white',
                    root: 'flex h-12 w-12 bg-white/10 p-0 hover:bg-transparent lg:hidden',
                  }}
                />
                <HeaderCartButton />
                <HeaderProfileButton />
              </div>
            </div>
          </div>
          <Categories
            categories={categories}
            className={{
              container: cn(
                'hidden md:flex',
                isHomepage && scrolled && 'hidden bg-black transition-all',
              ),
            }}
          />
          <section className={cn('custom-section', 'static flex bg-black py-2 md:hidden')}>
            <div className="flex w-full items-center justify-between">
              <MobileSidebar
                side="left"
                menu={menu[0] && menu[0].fields}
                menuArrow
                menuSeparator
                categories={categories}
              />
              <Link prefetchOnHover prefetch={false} href={'/'} aria-label="RSmoto – domov">
                <Image
                  src={generalLayout.header?.logo?.url ?? '/images/placeholder.png'}
                  alt={resolveImageAlt(
                    generalLayout?.header?.logo,
                    generalLayout?.contact?.company,
                  )}
                  width={0}
                  height={0}
                  className="max-h-12 min-h-12 w-auto object-contain object-center"
                />
              </Link>
              <div className="flex w-[65px] items-center justify-end">
                <SearchButton
                  variant="ghost"
                  className={{
                    icon: 'text-white',
                    root: 'w-12p-0 flex h-12 hover:bg-transparent',
                  }}
                />
              </div>
            </div>
          </section>
        </header>
      </MobileSearchProvider>
    );
}
